"use client";

import { useState, useEffect } from "react";
import type { Channel } from "../hooks/useChannels";
import type { UnreadStatus } from "../hooks/useUnreads";
import { ChannelListItem } from "./ChannelListItem";

interface Props {
  channels: Channel[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  collapsed: boolean;
  onToggle: () => void;
  getUnreadStatus?: (channelId: string) => UnreadStatus;
}

export function ChatSidebar({ channels, selectedId, onSelect, collapsed, onToggle, getUnreadStatus }: Props) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // On mobile: fixed overlay that slides over the messages
  // On desktop: inline flex sidebar that pushes content (existing behavior)
  const sidebarStyle: React.CSSProperties = isMobile
    ? {
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        width: 260,
        background: "var(--color-bg)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: 200,
        borderRight: "1px solid var(--color-border)",
        transform: collapsed ? "translateX(-100%)" : "translateX(0)",
        transition: "transform 0.2s ease",
      }
    : {
        width: collapsed ? 0 : 260,
        minWidth: collapsed ? 0 : 260,
        borderRight: collapsed ? "none" : "1px solid var(--color-border)",
        background: "var(--color-bg)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "width 0.2s ease, min-width 0.2s ease",
      };

  return (
    <>
      {/* Backdrop overlay on mobile when sidebar is open */}
      {isMobile && !collapsed && (
        <div
          onClick={onToggle}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.3)",
            zIndex: 199,
          }}
        />
      )}
      <aside style={sidebarStyle}>
        <div
          style={{
            padding: "0.6rem 0.75rem",
            fontSize: "0.7rem",
            fontWeight: 600,
            color: "var(--color-text-tertiary)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Channels
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {channels.map((ch) => (
            <ChannelListItem
              key={ch.id}
              channel={ch}
              selected={ch.id === selectedId}
              onClick={() => onSelect(ch.id)}
              unreadStatus={getUnreadStatus?.(ch.id) ?? "none"}
            />
          ))}
        </div>
      </aside>
    </>
  );
}
